import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Building2, ClipboardList, FileText, FlaskConical, LogOut, Package, Search, Users } from "lucide-react";

import ivoryHealthLogo from "@/assets/ivory-health-logo.webp";
import EmployeeManager from "@/components/admin/EmployeeManager";
import LabTestManager from "@/components/admin/LabTestManager";
import PackageManager from "@/components/admin/PackageManager";
import ReportQcQueue from "@/components/admin/ReportQcQueue";
import RequestsSection from "@/components/admin/RequestsSection";
import OrganisationsSection from "@/components/admin/OrganisationsSection";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { request } from "@/lib/api";

const SECTIONS = [
  { to: "/admin/requests", label: "Requests", icon: ClipboardList, needsClient: false },
  { to: "/admin/employees", label: "Employees", icon: Users, needsClient: true },
  { to: "/admin/packages", label: "Packages", icon: Package, needsClient: false },
  { to: "/admin/lab-tests", label: "Lab tests", icon: FlaskConical, needsClient: true },
  { to: "/admin/reports", label: "Reports QC", icon: FileText, needsClient: false },
  { to: "/admin/organisations", label: "Organisations", icon: Building2, needsClient: false },
];

export default function AdminShell() {
  const { token, user, signOut } = useAdminAuth();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState("");
  const [error, setError] = useState("");

  const loadClients = useCallback(async () => {
    try {
      const result = await request("/admin/clients", {}, token);
      setClients(result);
      setActiveClientId((current) => current || result[0]?.client_id || "");
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [token]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const activeClient = useMemo(
    () => clients.find((client) => client.client_id === activeClientId),
    [clients, activeClientId],
  );
  const current = SECTIONS.find((section) => location.pathname.startsWith(section.to));

  return (
    <div className="ops-shell" data-testid="ops-shell">
      <aside className="ops-sidebar">
        <div className="ops-brand">
          <img src={ivoryHealthLogo} alt="Ivory Health" />
          <div>
            <strong>Ivory Health</strong>
            <span>Ops</span>
          </div>
        </div>

        <nav className="ops-nav">
          {SECTIONS.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              className={({ isActive }) => `ops-nav__item ${isActive ? "is-active" : ""}`}
              data-testid={`ops-nav-${section.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <section.icon size={18} />
              <span>{section.label}</span>
            </NavLink>
          ))}
        </nav>

        <button type="button" className="ops-nav__item ops-nav__signout" onClick={signOut} data-testid="admin-sign-out-button">
          <LogOut size={18} />
          <span>Sign out</span>
        </button>
      </aside>

      <div className="ops-main">
        <header className="ops-topbar">
          <div className="ops-breadcrumb" data-testid="ops-breadcrumb">
            <span>Ops</span>
            <span aria-hidden="true">›</span>
            <strong>{current?.label || "Requests"}</strong>
          </div>

          <div className="ops-topbar__tools">
            {/* The organisation applies across every section, so it lives in
                the chrome rather than being asked for again on each page. */}
            <label className="ops-client-picker">
              <Building2 size={15} aria-hidden="true" />
              <select
                value={activeClientId}
                onChange={(event) => setActiveClientId(event.target.value)}
                aria-label="Organisation"
                data-testid="ops-client-select"
              >
                {clients.map((client) => (
                  <option value={client.client_id} key={client.client_id}>{client.name}</option>
                ))}
              </select>
            </label>
            <span className="ops-user" data-testid="admin-user-email">{user?.email}</span>
          </div>
        </header>

        <main className="ops-content">
          {error && <p className="form-error" role="alert" data-testid="admin-dashboard-error">{error}</p>}
          <Routes>
            <Route index element={<Navigate to="/admin/requests" replace />} />
            <Route path="requests" element={<RequestsSection token={token} clients={clients} activeClientId={activeClientId} />} />
            <Route path="employees" element={<EmployeeManager inline client={activeClient} token={token} onClientChanged={loadClients} />} />
            <Route path="packages" element={<PackageManager inline token={token} />} />
            <Route path="lab-tests" element={<LabTestManager inline token={token} clientId={activeClientId} clientName={activeClient?.name} />} />
            <Route path="reports" element={<ReportQcQueue inline token={token} />} />
            <Route path="organisations" element={<OrganisationsSection token={token} clients={clients} onChanged={loadClients} />} />
            <Route path="*" element={<Navigate to="/admin/requests" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
